import { DOCUMENT, isPlatformBrowser, NgClass, NgIf } from '@angular/common';
import { Component, Inject, Injectable, Input, OnChanges, OnInit, PLATFORM_ID, Renderer2, SimpleChanges } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { TruncatePipe } from '../../../../pipes/truncate.pipe';
import { AppSettings } from '../../../../service/AppSettings';
import { GigsumoService } from '../../../../service/gigsumo.service';
import { candidateModel } from '../../../../service/job.model';
import { getCandidateParams } from '../job-listing/job-listing.component';

@Injectable({
  providedIn: "root"
})
export class CandidateProvider {

  private candidateData: any = {};
  private isLoadingCandidates: boolean = false;
  private cachedCandidateResults: any[] = []; // Cache current page results

  // Last candidate the user viewed - used by the Similar Candidates widget to exclude the
  // candidate they navigated FROM. Lives on this root singleton so it survives the sidebar
  // being destroyed/recreated across navigations.
  previousCandidateId: string | null = null;

  // One-shot flag set by the Similar Candidates widget so a click ALWAYS re-calls the
  // candidate details API (findCandidatesForOpenSession), bypassing the in-cache skip that
  // normal card clicks use. Consumed (reset) on the next paramMap emission.
  forceListingRefresh = false;

  setData(v: any) {
    this.candidateData = v;
  }

  get getCandidate() {
    return this.candidateData;
  }

  setLoadingState(loading: boolean) {
    this.isLoadingCandidates = loading;
    console.log('🔄 CandidateProvider: setLoadingState =', loading);
  }

  get isLoading() {
    return this.isLoadingCandidates;
  }

  // Cache the current page's candidate results
  setCachedResults(results: any[]) {
    this.cachedCandidateResults = results || [];
    console.log('💾 CandidateProvider: Cached', this.cachedCandidateResults.length, 'candidate results');
  }

  // Get cached results (persists across component recreations)
  getCachedResults(): any[] {
    return this.cachedCandidateResults;
  }

  // Check if a specific candidate exists in cached results
  candidateExistsInCache(candidateId: string | null): boolean {
    if (!candidateId || !this.cachedCandidateResults || this.cachedCandidateResults.length === 0) {
      return false;
    }

    const exists = this.cachedCandidateResults.some((candidate: any) =>
      candidate.candidateId === candidateId ||
      candidate.id === candidateId ||
      candidate.candidateId?.toString() === candidateId?.toString()
    );

    console.log('🔍 CandidateProvider: Candidate', candidateId, exists ? 'EXISTS' : 'NOT FOUND', 'in cache');
    return exists;
  }

}


@Component({
  selector: 'app-candidate-card',
  imports: [NgIf, TruncatePipe, NgClass, RouterLink],
  templateUrl: './candidate-card.component.html',
  styleUrl: './candidate-card.component.scss',
  standalone: true,
  host: { ngSkipHydration: 'true' }
})
export class CandidateCardComponent implements OnInit, OnChanges {

  page: number = 1;
  currentQueryParams: any = {};

  constructor(private gigsumoService: GigsumoService,
    public candidateData: CandidateProvider,
    private _activateRoute: ActivatedRoute,
    private meta: Meta,
    private title: Title,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object
  ) {

    this.routeSub = this._activateRoute.paramMap.subscribe(params => {
      // URL is now `/talents/{role-slug}-[{city}-{state}-]{cid}` (lowercase, hyphens).
      // The card only needs a string that compares to `passValues.candidateId`
      // for the highlight class — the listing handles the canonical extraction.
      const slug = params.get('candidateId') ?? '';
      this.selectedItem = this.extractTrailingId(slug);
    });

    this._activateRoute.queryParams.subscribe(params => {
      this.page = params['page'] ?? 1;
      // Store all query params to preserve them when navigating
      this.currentQueryParams = { ...params };
    });
  }

  setMetaTags(data: any): void {
    const pageTitle = `${data.jobTitle} in ${data.city}, ${data.state}  - ${data.postedBehalfOf} | Gigsumo`;
    const description = `Join ${data.postedBehalfOf} as a ${data.jobTitle}. Required skills: ${data.primarySkills}. Location: ${data.city}, ${data.state}. ${data.experienceFrom}+ years of experience preferred.`;
    this.jobTitle = data.jobTitle;
    this.companyName = data.postedBehalfOf;
    this.location = data.city + ', ' + data.state
    if (data && data.primarySkills) {
      data.primarySkills.forEach((ele: any, index: number) => {
        if (index == 0) {
          this.skills = this.skills + ele
        } else {
          this.skills = this.skills + ', ' + ele
        }
      })
    }
    this.yearsOfExperience = data.experienceFrom
    // Set page title
    this.title.setTitle(pageTitle);
    this.meta.addTags([
      { name: 'description', content: `Created ${data.createdOn} Looking for a ${data.jobTitle} in ${data.city}, ${data.state} with ${data.primarySkills} and ${data.experienceTo} years of experience.` },
      { name: 'keywords', content: `${data.jobTitle}, ${data.city}, ${data.state}, ${data.primarySkills}, ${data.experienceTo} years experience` },
      { property: 'og:title', content: `${data.jobTitle} - ${data.city}, ${data.state}` },
      { property: 'og:description', content: `A job opportunity for ${data.jobTitle} with ${data.primarySkills} skills and ${data.experienceTo} years of experience.` },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://www.gigsumo.com/images/gigsumologo_bigface.png' },

      { property: 'og:type', content: 'website' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: pageTitle },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: 'https://www.gigsumo.com/images/gigsumologo_bigface.png' },
      { name: 'twitter:creator', content: '@GigSumoHQ' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
      { charset: 'UTF-8' },
      { name: 'robots', content: 'index, follow' },
      { name: 'author', content: 'Gigsumo Inc.' }
    ])

    if (isPlatformBrowser(this.platformId)) {
      this.meta.addTags([
        { property: 'og:url', content: window.location.href }
      ])
    }
    setTimeout(() => {
      this.setStructuredData(data);
    }, 300);
  }
  jobTitle: any;
  companyName: any;
  skills: any;
  location: any;
  yearsOfExperience: any;
  setStructuredData(data: any): void {
    const script = this.renderer.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      'title': this.jobTitle,
      'description': `Join ${this.companyName} as a ${this.jobTitle}. Required skills: ${this.skills}. Location: ${this.location}. ${this.yearsOfExperience}+ years of experience preferred.`,
      'identifier': {
        '@type': 'PropertyValue',
        'name': this.companyName,
        'value': '12345'
      },
      'datePosted': new Date().toISOString().split('T')[0], // Current date
      'jobLocation': {
        '@type': 'Place',
        'address': {
          '@type': 'PostalAddress',
          'streetAddress': '123 Tech Street',
          'addressLocality': this.location.split(',')[0],
          'addressRegion': this.location.split(',')[1]?.trim() || '',
          'postalCode': data.zipCode,
          'addressCountry': data.country
        }
      },
      'hiringOrganization': {
        '@type': 'Organization',
        'name': this.companyName,
        'sameAs': 'https://www.companywebsite.com'
      },
      'baseSalary': {
        '@type': 'MonetaryAmount',
        'currency': 'US Dollars',
        'value': {
          '@type': 'QuantitativeValue',
          'value': data.targetRateTo,
          'unitText': data.createdOn
        }
      }
    });
    if (isPlatformBrowser(this.platformId)) {
      this.renderer.appendChild(document.head, script);
    }
  }

  @Input() passValues: any;
  @Input('Obj') selectedCandidate !: candidateModel;
  routeSub = new Subscription
  ENVIRONMENT = environment;
  selectedItem: any;
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['passValues'] && changes['passValues'].currentValue != undefined) {
      this.passValues = changes['passValues'].currentValue;
      // this.onSyncWithJobSubmitSkiller();
    }
    if (changes['Obj'] && changes['Obj'].currentValue) {
      this.selectedItem = changes['Obj'].currentValue.candidateId;
      console.log('🎯 Card highlighting updated via ngOnChanges:', this.selectedItem);
    }
  }

  ngOnInit(): void {
    if (this.selectedCandidate) {
      this.selectedItem = this.selectedCandidate.candidateId;
    }
  }

  // ===== New design presentational helpers =====

  /** Formatted pay-rate range for the card meta row. */
  get rateText(): string {
    return this.gigsumoService.formatRateRange(
      this.passValues?.targetRateFrom,
      this.passValues?.targetRateTo,
      this.passValues?.country,
      this.passValues?.payType
    );
  }

  /** CSS state class suffix for the candidate status badge. */
  getStatusClass(status: string): string {
    return this.gigsumoService.getCandidateStatusClass(status);
  }

  /** Human label for the candidate status badge. */
  getStatusLabel(status: string): string {
    return this.gigsumoService.getCandidateStatusLabel(status);
  }
  shouldShowGetResume(_t6: any) {
    throw new Error('Method not implemented.');
  }
  duplicateresume: any;
  jobsupplytemplate: any;
  requestResume(_t6: any, _t7: boolean, arg2: any, arg3: any) {
    throw new Error('Method not implemented.');
  }
  downloadResume(_t6: any, arg1: any, arg2: any) {
    throw new Error('Method not implemented.');
  }
  connectionTemplate: any;
  chatuser(_t6: any, arg1: any) {
    throw new Error('Method not implemented.');
  }
  userType: any;
  shouldShowRequested(_t6: any): any {
    throw new Error('Method not implemented.');
  }
  apply(_t6: any, arg1: any, arg2: any) {
    throw new Error('Method not implemented.');
  }
  like(_t6: any) {
    throw new Error('Method not implemented.');
  }
  goTo(_t6: any, arg1: string) {
    throw new Error('Method not implemented.');
  }
  submissionFilterRoute(_t6: any) {
    throw new Error('Method not implemented.');
  }
  userId: any;
  filtersApplied: any;
  candidateStatus(arg0: string, _t6: any) {
    throw new Error('Method not implemented.');
  }
  deletecandidate(arg0: string) {
    throw new Error('Method not implemented.');
  }
  upgradeCandidate(_t6: any) {
    throw new Error('Method not implemented.');
  }
  edit(_t6: any, arg1: any) {
    throw new Error('Method not implemented.');
  }
  open(_t6: any) {
    throw new Error('Method not implemented.');
  }
  url = AppSettings.photoUrl;
  connect(_t6: any) {
    throw new Error('Method not implemented.');
  }
  getInitials(fullname: string): string {
    return this.gigsumoService.getInitials(fullname);
  }
  getColor(fullname: string): string {
    return this.gigsumoService.getColor(fullname);
  }

  // for first and last name
  getInitialName(firstname: string, lastname: string): string {
    return this.gigsumoService.getInitialsparam(firstname, lastname);
  }
  // for first and last name
  getColorName(firstname: string, lastname: string): string {
    return this.gigsumoService.getColorparam(firstname, lastname);
  }

  getTextColorName(firstname: string, lastname: string): string {
    return this.gigsumoService.getTextColorParam(firstname, lastname);
  }

  getLoginParams(job:any){
      return getCandidateParams(job);
  } 
  
  getRouteURL(candidateData: any) {
    // New SEO pattern: /talents/{role-slug}[-{city}-{state}]-{CID}
    return ['/talents', this.gigsumoService.buildCandidateSlug(candidateData)];
  }

  /** Best-effort ID extraction for card highlighting. Tolerates legacy
   *  `_`-separated slugs and the new `-`-separated lowercase form. */
  private extractTrailingId(slug: string): string {
    if (!slug) return '';
    const decoded = decodeURIComponent(slug);
    const trailing = decoded.split(/[-_]/).pop() || '';

    // CID + digits: uppercase prefix to match backend records.
    const cid = (trailing.match(/^cid\d+$/i) || decoded.match(/cid\d+/i) || [])[0];
    if (cid) return cid.toUpperCase();

    // Two-letter alpha prefix + hyphen + digits (MK-…, etc).
    const tail2 = decoded.split(/[-_]/).slice(-2).join('-');
    const dashed = (tail2.match(/^[a-z]{2,3}-\d+$/i) || decoded.match(/[a-z]{2,3}-\d+/i) || [])[0];
    if (dashed) return dashed.replace(/^([a-z]{2,3})/i, (m) => m.toUpperCase());

    return trailing;
  }
}
